/**
 * Session Notification Store
 *
 * Manages session-scoped notifications that persist across related pages
 * but are cleared when the browser is closed or the session ends.
 *
 * Use cases:
 * - Call flow: notifications shared between call page, incident report, and reports modal
 * - Report generation: notifications during multi-step report creation
 */

import type { NotificationSession, SessionType } from './types';
import logger from '@/lib/logger';

export interface SessionNotification {
  id: string;
  title: string;
  body?: string;
  category: string;
  priority: string;
  toastStyle: string;
  session: NotificationSession;
  createdAt: string;
  isRead: boolean;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
}

const STORAGE_KEY = 'session_notifications';

/**
 * Get all session notifications from storage
 */
function getAllNotifications(): SessionNotification[] {
  if (typeof window === 'undefined') return [];

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    logger.error('SessionStore', 'Failed to parse notifications', { error });
    return [];
  }
}

/**
 * Save all notifications to storage
 */
function saveNotifications(notifications: SessionNotification[]): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  } catch (error) {
    logger.error('SessionStore', 'Failed to save notifications', { error });
  }
}

/**
 * Add a notification to session storage
 */
export function addSessionNotification(notification: Omit<SessionNotification, 'id' | 'createdAt' | 'isRead'>): SessionNotification {
  const fullNotification: SessionNotification = {
    ...notification,
    id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    createdAt: new Date().toISOString(),
    isRead: false,
  };

  const notifications = getAllNotifications();
  notifications.push(fullNotification);
  saveNotifications(notifications);

  return fullNotification;
}

/**
 * Get notifications for a specific session
 */
export function getSessionNotifications(session: NotificationSession): SessionNotification[] {
  return getAllNotifications().filter(
    (n) => n.session.type === session.type && n.session.id === session.id
  );
}

/**
 * Get notifications by session type (e.g., all 'call' notifications)
 */
export function getNotificationsByType(type: SessionType): SessionNotification[] {
  return getAllNotifications().filter((n) => n.session.type === type);
}

/**
 * Get unread count for a session
 */
export function getUnreadCount(session: NotificationSession): number {
  return getSessionNotifications(session).filter((n) => !n.isRead).length;
}

/**
 * Mark a notification as read
 */
export function markAsRead(notificationId: string): void {
  const notifications = getAllNotifications();
  const notification = notifications.find((n) => n.id === notificationId);
  if (notification) {
    notification.isRead = true;
    saveNotifications(notifications);
  }
}

/**
 * Mark all notifications in a session as read
 */
export function markSessionAsRead(session: NotificationSession): void {
  const notifications = getAllNotifications();
  let changed = false;

  notifications.forEach((n) => {
    if (n.session.type === session.type && n.session.id === session.id && !n.isRead) {
      n.isRead = true;
      changed = true;
    }
  });

  if (changed) {
    saveNotifications(notifications);
  }
}

/**
 * Clear all notifications for a session
 */
export function clearSession(session: NotificationSession): void {
  const notifications = getAllNotifications().filter(
    (n) => !(n.session.type === session.type && n.session.id === session.id)
  );
  saveNotifications(notifications);
}

/**
 * Clear all notifications of a session type
 */
export function clearSessionType(type: SessionType): void {
  const notifications = getAllNotifications().filter((n) => n.session.type !== type);
  saveNotifications(notifications);
}

/**
 * Clear all session notifications
 */
export function clearAllSessions(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Remove notifications older than maxAge (in milliseconds)
 */
export function cleanupOldNotifications(maxAgeMs: number = 24 * 60 * 60 * 1000): number {
  const cutoff = Date.now() - maxAgeMs;
  const notifications = getAllNotifications();
  const originalCount = notifications.length;

  const filtered = notifications.filter((n) => {
    const createdAt = new Date(n.createdAt).getTime();
    return createdAt > cutoff;
  });

  if (filtered.length !== originalCount) {
    saveNotifications(filtered);
  }

  return originalCount - filtered.length;
}

/**
 * Subscribe to session notification changes
 * Returns an unsubscribe function
 */
export function subscribeToSession(
  session: NotificationSession,
  callback: (notifications: SessionNotification[]) => void
): () => void {
  // Initial call
  callback(getSessionNotifications(session));

  // Listen for storage events (cross-tab sync)
  const handler = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      callback(getSessionNotifications(session));
    }
  };

  window.addEventListener('storage', handler);

  return () => {
    window.removeEventListener('storage', handler);
  };
}
